import React, { useEffect, useMemo, useState } from 'react';
import {
	Badge, IconButton, Button, Box, Dialog, DialogContent, DialogActions,
	Card, CardActionArea, CardContent, Typography, Tooltip, DialogTitle
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TuneOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Clear';
import { useLanguage } from 'Context/LanguageContext';
import { SmartTitleTypography } from '~/FielderElements/SmartTaskTable/SmartStyles';
import { FielderHard, FielderPink } from '@core/theme/baseTheme';

const StyledBadge = styled(Badge)(() => ({
	'& .MuiBadge-badge': { backgroundColor: '#dc5c87', color: 'white' },
}));

const SetupDateFilter = ({ isLoading, value, onApplyPreset, datePresets }) => {
	const lan = useLanguage();
	const cardTitles = useMemo(() => ({
		daily: lan.daily,
		weekly: lan.weekly,
		monthly: lan.monthly,
		annually: lan.annually,
		personalized: lan.customize,
	}), [lan.daily, lan.weekly, lan.monthly, lan.annually, lan.customize]);
	const descriptions = useMemo(() => ({
		daily: lan.thisDay || 'Use the current day.',
		weekly: lan.thisWeek || 'Use the current week.',
		monthly: lan.thisMonth || 'Use the current month.',
		annually: lan.thisYear || 'Use the current year.',
		personalized: lan.pickRange || 'Choose a custom range.',
	}), [lan.thisDay, lan.thisWeek, lan.thisMonth, lan.thisYear, lan.pickRange]);
	const cards = useMemo(() => ([
		{ value: datePresets.DAY, title: cardTitles.daily, description: descriptions.daily },
		{ value: datePresets.WEEK, title: cardTitles.weekly, description: descriptions.weekly },
		{ value: datePresets.MONTH, title: cardTitles.monthly, description: descriptions.monthly },
		{ value: datePresets.YEAR, title: cardTitles.annually, description: descriptions.annually },
		{ value: datePresets.CUSTOM, title: cardTitles.personalized, description: descriptions.personalized },
	]), [
		cardTitles.daily, descriptions.daily, cardTitles.weekly, descriptions.weekly, cardTitles.monthly,
		descriptions.monthly, cardTitles.annually, descriptions.annually, cardTitles.personalized, descriptions.personalized
	]);
	const [openModal, setOpenModal] = useState(false);
	const [selectedValue, setSelectedValue] = useState(value);
	useEffect(() => { setSelectedValue(value); }, [value]);
	const handleOpen = () => setOpenModal(true);
	const handleClose = () => { setSelectedValue(value); setOpenModal(false); };
	const handleApply = () => { onApplyPreset?.(selectedValue); setOpenModal(false); };
	return (
		<>
			<Tooltip title={lan.configureSearchParameters}>
				<StyledBadge
					badgeContent={value !== datePresets.CUSTOM && !isLoading ? 1 : 0}
					invisible={value === datePresets.CUSTOM}
				>
					<IconButton
						id="btn-setup-date-filter"
						onClick={handleOpen}
						disabled={isLoading}
						size="small">
						<TuneOutlined />
					</IconButton>
				</StyledBadge>
			</Tooltip>

			<Dialog
				open={openModal}
				onClose={handleClose}
				maxWidth={false}
				slotProps={{
					paper: {
						sx: {
							position: 'relative', overflow: 'visible', borderRadius: 2, minWidth: 600, maxWidth: '90vw', width: 'auto'
						},
					},
				}}
			>
				<IconButton
					onClick={handleClose}
					size="small"
					sx={{
						position: 'absolute', top: -8, right: -8, width: 24, height: 24, bgcolor: '#fff',
						zIndex: 10, boxShadow: 2, p: 0, '&:hover': { bgcolor: '#dc5c87' },
					}}
				>
					<CloseIcon fontSize="small" sx={{ color: '#dc5c87', '&:hover': { color: '#fff' } }} />
				</IconButton>
				<DialogTitle>
					<Box display="flex" justifyContent="flex-start">
						<TuneOutlined style={{ width: 25, height: 25, marginRight: 6, marginTop: 2 }} />
						<SmartTitleTypography variant={'subtitle1'}>
							{lan.configureSearchParameters}
						</SmartTitleTypography>
					</Box>
				</DialogTitle>
				<DialogContent dividers sx={{ p: 2 }}>
					<Box
						sx={{
							width: '100%',
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
							gap: 2,
						}}
					>
						{cards.map((card) => {
							const active = selectedValue === card.value;
							return (
								<Card key={card.value} elevation={active ? 2 : 1}>
									<CardActionArea
										onClick={() => setSelectedValue(card.value)}
										data-active={active ? '' : undefined}
										sx={{
											height: '100%',
											'&[data-active]': {
												bgcolor: FielderHard,
												color: '#fff',
												outline: '2px solid',
												outlineColor: FielderPink,
											},
										}}
									>
										<CardContent sx={{ height: '100%' }}>
											<Typography variant="h6" component="div" gutterBottom>
												{card.title}
											</Typography>
											<Typography variant="body2">
												{card.description}
											</Typography>
										</CardContent>
									</CardActionArea>
								</Card>
							);
						})}
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'flex-end', px: 1, py: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
						<Button variant="contained" onClick={handleApply} size="small" sx={{ backgroundColor: '#dc5c87' }}>
							{lan.apply}
						</Button>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};
export default SetupDateFilter;
